/**
 * Options for capturing canvas thumbnails.
 */
export interface CaptureOptions {
  /** Target width for the thumbnail in pixels. Default: 220 */
  targetWidth?: number;
  /** JPEG quality (0-1). Default: 0.6 */
  quality?: number;
  /** Whether to include images in the capture. Default: true */
  includeImages?: boolean;
  /** Whether to include background images. Default: true */
  includeBackgroundImages?: boolean;
}

/**
 * Capture a lightweight base64 JPEG thumbnail of the report canvas.
 * - Targets the '#report-canvas' element
 * - Scales down to targetWidth while preserving aspect ratio
 * - Filters out UI overlays (moveable/selecto/toolbars)
 *
 * @param options - Configuration options for the capture
 * @returns Base64 data URL of the thumbnail, or null if capture failed
 */
export async function captureCanvasThumbnail(
  options: CaptureOptions = {}
): Promise<string | null> {
  const {
    targetWidth = 220,
    quality = 0.6,
    includeImages = true,
    includeBackgroundImages = true,
  } = options;

  // Dynamically import to avoid SSR import side-effects
  const { toJpeg } = await import("html-to-image");
  const element = document.getElementById("report-canvas");
  if (!element) {
    console.warn("[captureCanvasThumbnail] report-canvas element not found");
    return null;
  }

  // Use layout dimensions, not bounding rect (avoids transforms/zoom)
  const width = (element as HTMLElement).offsetWidth || element.clientWidth;
  const height = (element as HTMLElement).offsetHeight || element.clientHeight;
  if (!width || !height) {
    console.warn("[captureCanvasThumbnail] Element has no dimensions:", {
      width,
      height,
    });
    return null;
  }

  const scale = targetWidth / width;
  const bg =
    getComputedStyle(element).backgroundColor &&
    getComputedStyle(element).backgroundColor !== "rgba(0, 0, 0, 0)"
      ? getComputedStyle(element).backgroundColor
      : "#ffffff";

  // UI overlay class patterns to exclude from capture
  const blockedClassPatterns = [
    "moveable-",
    "selecto-",
    "selecto-wrapper",
    "report-floating-toolbar",
    "zoom-controls",
    "react-draggable-transparent-selection",
  ];

  /**
   * Filter function to exclude UI elements from capture.
   * By default, includes images and background images for richer thumbnails.
   */
  const filterNode = (node: Node): boolean => {
    if (!(node instanceof Element)) return true;

    const className = node.className?.toString() ?? "";
    const tag = (node as Element).tagName?.toLowerCase?.() || "";

    // Exclude UI overlays and selection handles
    if (blockedClassPatterns.some((pattern) => className.includes(pattern))) {
      return false;
    }

    // Handle <img> elements based on options
    if (tag === "img" && !includeImages) {
      return false;
    }

    // Handle background-image elements based on options
    if (!includeBackgroundImages) {
      try {
        const cs = getComputedStyle(node);
        if (cs.backgroundImage && cs.backgroundImage !== "none") {
          return false;
        }
      } catch {
        // ignore getComputedStyle errors
      }
    }

    return true;
  };

  // First attempt: try with images included (if enabled)
  try {
    const dataUrl = await toJpeg(element, {
      quality,
      canvasWidth: Math.round(width * scale),
      canvasHeight: Math.round(height * scale),
      width: Math.round(width),
      height: Math.round(height),
      backgroundColor: bg,
      style: {
        transform: "none",
        transformOrigin: "top left",
        boxShadow: "none",
        outline: "none",
        backgroundColor: bg,
      },
      filter: filterNode,
      pixelRatio: 1,
      skipFonts: true,
      // Enable CORS handling for cross-origin images
      cacheBust: true,
      // Include credentials for same-origin images
      includeQueryParams: true,
    });
    return dataUrl;
  } catch (firstError) {
    // If first attempt fails (likely CORS), retry without images
    if (includeImages || includeBackgroundImages) {
      console.warn(
        "[captureCanvasThumbnail] First capture failed, retrying without images:",
        firstError
      );
      try {
        const fallbackDataUrl = await toJpeg(element, {
          quality,
          canvasWidth: Math.round(width * scale),
          canvasHeight: Math.round(height * scale),
          width: Math.round(width),
          height: Math.round(height),
          backgroundColor: bg,
          style: {
            transform: "none",
            transformOrigin: "top left",
            boxShadow: "none",
            outline: "none",
            backgroundColor: bg,
          },
          filter: (node) => {
            if (!(node instanceof Element)) return true;
            const className = node.className?.toString() ?? "";
            const tag = (node as Element).tagName?.toLowerCase?.() || "";

            // Exclude UI overlays
            if (
              blockedClassPatterns.some((pattern) => className.includes(pattern))
            ) {
              return false;
            }

            // Exclude all images in fallback mode
            if (tag === "img") return false;

            // Exclude all background-images in fallback mode
            try {
              const cs = getComputedStyle(node);
              if (cs.backgroundImage && cs.backgroundImage !== "none") {
                return false;
              }
            } catch {
              // ignore
            }

            return true;
          },
          pixelRatio: 1,
          skipFonts: true,
        });
        return fallbackDataUrl;
      } catch (fallbackError) {
        console.warn(
          "[captureCanvasThumbnail] Fallback capture also failed:",
          fallbackError
        );
        return null;
      }
    }

    console.warn("[captureCanvasThumbnail] Failed to capture thumbnail:", firstError);
    return null;
  }
}

/**
 * Wait for an idle period up to timeoutMs. Returns true if idle occurred, false on timeout.
 */
export function waitForIdle(timeoutMs: number = 100): Promise<boolean> {
  return new Promise((resolve) => {
    const fallback = setTimeout(() => resolve(false), timeoutMs);
    const ric =
      (window as any).requestIdleCallback ||
      ((cb: any) => setTimeout(() => cb({}), 1));
    ric(() => {
      clearTimeout(fallback);
      resolve(true);
    });
  });
}


